import { useState, useEffect, useCallback } from "react";
import API_BASE_URL from "../config";

const useStockData = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState("");

  useEffect(() => {
    fetchCategories();
    fetchAllProducts();
  }, []);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/categories/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Hiba a kategóriák betöltésekor.");
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error("Kategória betöltési hiba:", error);
    }
  };

  const fetchCategoryById = useCallback(async (categoryId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Hiba a kategória betöltésekor.");
      const data = await response.json();
      setSelectedCategoryName(data.name); // 🔹 Beállítjuk a kategória nevét
      return data.name;
    } catch (error) {
      console.error("Kategória betöltési hiba:", error);
      return "Ismeretlen kategória"; // Ha hiba van, adjunk egy alapértéket
    }
  }, []);

  const fetchProductsByCategory = useCallback(async (categoryId) => {
    try {
      if (!categoryId) return;

      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/categories/${categoryId}/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Hiba a termékek betöltésekor.");
      const data = await response.json();
      setProducts(data);
      setSelectedCategory(categoryId);

      // 🔹 **Lekérjük a kategória nevét, ha még nincs beállítva**
      if (!selectedCategoryName) {
        await fetchCategoryById(categoryId);
      }
    } catch (error) {
      console.error("Termék betöltési hiba:", error);
    }
  }, [fetchCategoryById, selectedCategoryName]);

  // 🔥 **ÚJ: Összes termék lekérése (nem csak kategória szerint)**
  const fetchAllProducts = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Hiba a termékek betöltésekor.");
      const data = await response.json();
      setAllProducts(data);
    } catch (error) {
      console.error("Hiba az összes termék betöltésekor:", error);
    }
  };

  const addCategory = async (categoryData) => {
    try {
      const token = localStorage.getItem("token");

      const formData = new FormData();
      formData.append("Name", categoryData.name);
      if (categoryData.imageFile) {
        formData.append("ImageFile", categoryData.imageFile);
      }

      const response = await fetch(`${API_BASE_URL}/api/categories`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`, // Autentikáció
        },
        body: formData, // A FormData használata a kéréshez
      });

      if (!response.ok) throw new Error("Hiba a kategória hozzáadásánál.");

      await fetchCategories();
    } catch (error) {
      console.error("Eroare:", error);
    }
  };

  const updateCategory = async (categoryId, updatedData) => {
    try {
      const token = localStorage.getItem("token");

      const formData = new FormData();
      formData.append("Name", updatedData.name);

      if (updatedData.imageFile) {
        formData.append("ImageFile", updatedData.imageFile);
      }

      formData.append("isImageDeleted", updatedData.isImageDeleted ? "true" : "false");

      console.log("📤 Küldött adatok:", Object.fromEntries(formData.entries())); // 🔥 Debug log

      const response = await fetch(`${API_BASE_URL}/api/categories/${categoryId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) throw new Error("Hiba a kategória frissítésénél.");

      await fetchCategories(); // 🔄 Frissítsük a kategóriák listáját
      await fetchCategoryById(categoryId);
    } catch (error) {
      console.error("Hiba:", error);
    }
  };

  const deleteCategory = async (categoryId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/categories/${categoryId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Eroare la ștergerea categoriei.");

      await fetchCategories(); // 🔄 Frissítjük a listát
    } catch (error) {
      console.error("Eroare:", error);
    }
  };

  const updateProduct = async (productId, updatedData, isMarmuristPrice) => {
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();

      formData.append("Name", updatedData.name);
      formData.append("AvailableStock", parseFloat(updatedData.availableStock)); // 🔥 Számra alakítás!
      formData.append("CategoryId", parseInt(updatedData.categoryId)); // 🔥 Számra alakítás!
      formData.append("UnitType", updatedData.unitType);

      // 🔥 Mindig küldjük el mindkét árat, de az aktívat frissítjük!
      formData.append("Price", parseFloat(updatedData.price) || 0);
      formData.append("PriceMarmurist", parseFloat(updatedData.priceMarmurist) || 0);
      formData.append("CostPrice", parseFloat(updatedData.costPrice) || 0);

      if (updatedData.imageFile) {
        formData.append("ImageFile", updatedData.imageFile);
      }

      // 🔥 Küldjük az isImageDeleted értékét számként (1 = true, 0 = false)
      formData.append("isImageDeleted", updatedData.isImageDeleted ? "true" : "false");

      const response = await fetch(`${API_BASE_URL}/api/products/${productId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error("Hiba a válaszból:", errorData);
        throw new Error("Hiba a termék frissítésénél.");
      }

      await fetchProductsByCategory(updatedData.categoryId);
    } catch (error) {
      console.error("Hiba:", error);
    }
  };

  const addProduct = async (formData) => {
    try {
      const token = localStorage.getItem("token");

      const response = await fetch(`${API_BASE_URL}/api/products`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`, // 🔥 NE add meg a Content-Type-ot!
        },
        body: formData, // 🔥 FormData küldése helyesen
      });

      if (!response.ok) throw new Error("Hiba a termék hozzáadásánál.");

      await fetchProductsByCategory(selectedCategory); // 🔄 Frissítsük a listát
    } catch (error) {
      console.error("Hiba:", error);
    }
  };

  const deleteProduct = async (productId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/products/${productId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Hiba a termék törlésekor.");

      await fetchProductsByCategory(selectedCategory); // 🔄 Frissítsük a listát
    } catch (error) {
      console.error("Hiba:", error);
    }
  };

  const refreshStockData = async () => {
    await fetchAllProducts(); // Újratöltjük az összes terméket
  };
  return { categories, products, allProducts, selectedCategory, selectedCategoryName, fetchCategories, fetchProductsByCategory, fetchCategoryById, fetchAllProducts, addCategory, updateCategory, deleteCategory, updateProduct, addProduct, deleteProduct, refreshStockData };

};

export default useStockData;
